import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { BrandingService } from './branding.service';
import { PermissionService } from './permission.service';
import { ReferenceDataService } from './reference-data.service';
import { getTenantIdFromRouter, isNotificationReplyRoute } from '../utils/tenant-route.util';

/**
 * Keeps stored tenant id and tenant-scoped caches aligned when `:tenantId` in the URL changes
 * (e.g. QA edits the slug and reloads, or navigates between tenants). Does not add a tenant switcher UI.
 */
@Injectable({ providedIn: 'root' })
export class TenantRouteSyncService {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly referenceDataService = inject(ReferenceDataService);
  private readonly brandingService = inject(BrandingService);

  /** Last tenant from route we reconciled in this SPA session (null until first tenant navigation). */
  private lastSyncedRouteTenantId: string | null = null;

  constructor() {
    const destroyRef = inject(DestroyRef);

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => this.reconcileTenantFromRoute());
  }

  private reconcileTenantFromRoute(): void {
    const routeTenant = getTenantIdFromRouter(this.router);
    if (!routeTenant) {
      return;
    }

    // Email quick-reply uses the reply token, not a JWT — skip permission/branding refresh.
    if (isNotificationReplyRoute(this.router.url)) {
      if (this.authService.getStoredTenantId() !== routeTenant) {
        this.authService.setStoredTenantId(routeTenant);
      }
      this.lastSyncedRouteTenantId = routeTenant;
      return;
    }

    const stored = this.authService.getStoredTenantId();
    // Do not treat initial session (lastSynced null) as a tenant switch when URL already matches
    // stored tenant — that caused refreshPermissions() to clear the cache on every browser
    // reload before the sidebar read permissions, disabling Leads/Opps/Workboards until next nav.
    const storedMismatch = stored !== routeTenant;
    const navigatedToDifferentTenant =
      this.lastSyncedRouteTenantId !== null &&
      this.lastSyncedRouteTenantId !== routeTenant;
    const tenantContextChanged = storedMismatch || navigatedToDifferentTenant;

    if (stored !== routeTenant) {
      this.authService.setStoredTenantId(routeTenant);
    }

    if (tenantContextChanged) {
      this.brandingService.clearGlobalBranding();
      this.referenceDataService.clearTenantScopedCaches();
      this.permissionService.refreshPermissions().subscribe({
        error: () => {},
      });
    }

    this.lastSyncedRouteTenantId = routeTenant;
  }
}
